// src/components/common/DateRangeButton.tsx
import React, { useState, useRef } from 'react';
import { ChevronDown } from './icons';
import { formatDateToString } from '../../utils/dateUtils';

interface DateRangeButtonProps {
  dateText: string;                    // 顯示的日期範圍文字
  currentDate: Date;                   // 當前選中的日期
  onDateSelect: (date: Date) => void;  // 日期選擇回調
  isMobile?: boolean;                  // 是否為行動裝置
  className?: string;                  // 自訂類名
}

/**
 * DateRangeButton - Calendar 視圖的日期選擇按鈕
 *
 * 功能：
 * - 顯示當前日期範圍
 * - 點擊打開原生日期選擇器
 * - 響應式設計：桌面端和行動端不同樣式
 * - 可訪問性支援
 */
const DateRangeButton: React.FC<DateRangeButtonProps> = ({
  dateText,
  currentDate,
  onDateSelect,
  isMobile = false,
  className = ''
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  // 處理按鈕點擊
  const handleClick = () => {
    setShowDatePicker(true);
    // 延遲打開 date picker，確保 input 已渲染
    setTimeout(() => {
      if (dateInputRef.current) {
        dateInputRef.current.showPicker();
      }
    }, 50);
  };

  // 處理日期選擇
  const handleDateChange = (dateString: string) => {
    const selectedDate = new Date(dateString);
    onDateSelect(selectedDate);
    setShowDatePicker(false);
  };

  // 桌面端樣式
  const desktopStyles = `
    px-3 py-1.5
    hover:bg-gray-50
    text-gray-800
    rounded-lg
    transition-all duration-150
    border border-transparent 
    cursor-pointer
    flex items-center gap-1.5
    font-semibold
  `;

  // 行動端樣式
  const mobileStyles = `
    px-4 py-2
    bg-sky-50 active:bg-sky-100
    text-sky-700
    rounded-full
    font-semibold
    shadow-sm
    transition-all duration-150
    active:scale-95
    cursor-pointer
  `;

  return (
    <>
      <button
        onClick={handleClick}
        className={`${isMobile ? mobileStyles : desktopStyles} ${className}`}
        aria-label="Select date"
        type="button"
      >
        <span>{dateText}</span>
        {!isMobile && <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {/* 隱藏的日期選擇器 */}
      {showDatePicker && (
        <div className="sr-only">
          <input
            ref={dateInputRef}
            type="date"
            value={formatDateToString(currentDate)}
            onChange={(e) => handleDateChange(e.target.value)}
            onBlur={() => setShowDatePicker(false)}
          />
        </div>
      )}
    </>
  );
};

export default DateRangeButton;
